import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Download,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  ExternalLink,
  CreditCard,
  Calendar,
  DollarSign,
  User,
  Wallet,
  ArrowRight,
  ShieldCheck,
  Zap,
  Globe,
} from 'lucide-react';
import { jsPDF } from 'jspdf';
// White-on-dark lockup (mark + wordmark), for the violet header.
import maxelpayLogoLight from '../assets/maxelpay-logo-light.svg';
import autoTable from 'jspdf-autotable';
import { formatUSD, formatCrypto } from '../utils/formatAmount';
import '../styles/responsive/pages/PublicInvoiceView.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PublicInvoiceView = () => {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/invoices/public/${linkId}`);
        const data = await response.json();

        if (data.success && data.data) {
          const inv = data.data;

          let parsedLineItems = inv.lineItems;
          if (typeof parsedLineItems === 'string') {
            try {
              parsedLineItems = JSON.parse(parsedLineItems);
            } catch (e) {
              console.error('Failed to parse lineItems:', e);
              parsedLineItems = [];
            }
          }
          if (!Array.isArray(parsedLineItems)) {
            parsedLineItems = [];
          }

          const mappedInvoice = {
            id: inv.invoiceId,
            linkId: inv.linkId,
            customer_name: inv.customerName,
            customer_email: inv.customerEmail,
            amount: parseFloat(inv.amount) || 0,
            originalAmount: parseFloat(inv.originalAmount) || parseFloat(inv.amount) || 0,
            currency: inv.currency || 'USD',
            status: inv.status,
            due_date: inv.dueDate,
            created_at: inv.createdAt,
            finalized_at: inv.finalizedAt,
            paid_at: inv.paidAt,
            payment_method: inv.paymentMethod || 'Crypto Payment',
            network: inv.network,
            token: inv.token,
            amount_paid: parseFloat(inv.amountPaid) || 0,
            amount_remaining: parseFloat(inv.amountRemaining) || 0,
            tx_hash: inv.txHash,
            receiving_address: inv.receivingAddress,
            description: inv.description,
            items: parsedLineItems.length > 0
              ? parsedLineItems.map(item => ({
                  description: item.description,
                  quantity: item.quantity || 1,
                  price: parseFloat(item.unit_price) || parseFloat(item.unitPrice) || 0,
                  amount: parseFloat(item.amount) || 0,
                }))
              : [{
                  description: inv.description || 'Invoice Payment',
                  quantity: 1,
                  price: parseFloat(inv.originalAmount) || parseFloat(inv.amount) || 0,
                  amount: parseFloat(inv.originalAmount) || parseFloat(inv.amount) || 0,
                }],
            enabled_payment_methods: inv.enabledPaymentMethods || [],
            metadata: inv.metadata || {},
            merchant: inv.merchant || null,
          };
          setInvoice(mappedInvoice);

          if (inv.amountPaid > 0) {
            fetchTransactions(inv.invoiceId);
          }
        } else {
          setError(data.message || 'Invoice not found');
          setErrorCode(data.errorCode);
        }
      } catch (err) {
        console.error('Failed to fetch invoice:', err);
        setError('Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };

    const fetchTransactions = async (invoiceId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/invoices/public/${linkId}/transactions`);
        const data = await response.json();
        if (data.success && data.data?.transactions) {
          setTransactions(data.data.transactions);
        }
      } catch (err) {
        console.error('Failed to fetch transactions:', err);
      }
    };

    fetchInvoice();
  }, [linkId]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handlePayNow = () => {
    navigate(`/pay/invoice/${linkId}`);
  };


  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="status-icon success" />;
      case 'overpaid':
        return <AlertCircle className="status-icon overpaid" />;
      case 'overdue':
        return <AlertCircle className="status-icon danger" />;
      case 'partially_paid':
        return <Clock className="status-icon warning" />;
      default:
        return <Clock className="status-icon warning" />;
    }
  };

  if (loading) {
    return (
      <div className="public-invoice-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <span>Loading invoice...</span>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-invoice-page">
        <div className="error-state">
          <FileText size={48} />
          <h2>{errorCode === 'ALREADY_PAID' ? 'Invoice Already Paid' : 'Invoice Not Found'}</h2>
          <p>{error}</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="public-invoice-page">
        <div className="error-state">
          <FileText size={48} />
          <h2>Invoice Not Found</h2>
          <p>The invoice you're looking for doesn't exist or has been deleted.</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  const canPay = ['open', 'pending', 'partially_paid', 'paid_unsupported_token'].includes(invoice.status);

  /* The header now carries the MaxelPay lockup rather than the merchant's mark,
   * so the merchant is named under the invoice number instead. An invoice still
   * has to state who issued it; dropping the name would remove that.
   * merchantLogo is no longer read anywhere, so it is gone rather than left as
   * an unused binding. */
  const merchantName = invoice.merchant?.businessName || '';

  return (
    <div className="public-invoice-page">
      <div className="invoice-container">
        <div className="invoice-header">
          {/* Decorative only, and aria-hidden for that reason. Built from CSS
              gradients rather than an image so it scales with the header and
              adds no request. */}
          <span className="piv-wave" aria-hidden="true" />
          <span className="piv-orb piv-orb--a" aria-hidden="true" />
          <span className="piv-orb piv-orb--b" aria-hidden="true" />

          <div className="piv-head-top">
            <div className="brand">
              {/* Full lockup rather than a mark beside the word set in Poppins,
                  matching the checkout header. */}
              <img className="brand-logo" src={maxelpayLogoLight} alt="MaxelPay" />
              {/* <span className="brand-tag">Crypto Payment Gateway</span> */}
            </div>
            <span className={`status-badge ${invoice.status}`}>
              {getStatusIcon(invoice.status)}
              {invoice.status.replace('_', ' ').charAt(0).toUpperCase() + invoice.status.replace('_', ' ').slice(1)}
            </span>
          </div>

          <div className="piv-head-id">
            <span className="piv-head-id__label">Invoice Number</span>
            <span className="piv-head-id__value">{invoice.id}</span>
          </div>

          {merchantName && (
            <div className="piv-head-from">
              Billed by <strong>{merchantName}</strong>
            </div>
          )}

          <div className="piv-head-tags">
            <span>Secure</span>
            <i aria-hidden="true">&bull;</i>
            <span>Fast</span>
            <i aria-hidden="true">&bull;</i>
            <span>Global</span>
          </div>
        </div>

        <div className="invoice-info-bar">
          <div className="info-item">
            <span className="piv-tile"><Calendar size={18} /></span>
            <span className="info-item__body">
              <label>Issue Date</label>
              <span>{formatDateShort(invoice.created_at)}</span>
            </span>
          </div>
          <div className="info-item">
            <span className="piv-tile"><Calendar size={18} /></span>
            <span className="info-item__body">
              <label>Due Date</label>
              <span className={invoice.status === 'overdue' ? 'overdue' : ''}>
                {formatDateShort(invoice.due_date)}
              </span>
            </span>
          </div>
          <div className="info-item amount-due">
            <span className="piv-tile"><DollarSign size={18} /></span>
            <span className="info-item__body">
              <label>{invoice.status === 'overpaid' ? 'Overpaid By' : 'Amount Due'}</label>
              <span className={`amount ${invoice.status === 'overpaid' ? 'overpaid' : ''}`}>
                ${invoice.status === 'overpaid' ? formatUSD(invoice.amount_paid - invoice.originalAmount) : formatUSD(invoice.amount)} <small>{invoice.currency}</small>
              </span>
            </span>
          </div>
        </div>

        <div className="bill-to-section">
          <div className="bill-to">
            <span className="piv-tile"><User size={18} /></span>
            <div className="bill-to__body">
              <h3>Bill To</h3>
              <p className="customer-name">{invoice.customer_name}</p>
              <p className="customer-email">{invoice.customer_email}</p>
            </div>
          </div>
          {invoice.description && (
            <div className="invoice-description">
              <span className="piv-tile"><CreditCard size={18} /></span>
              <div className="bill-to__body">
                <h3>Payment Type</h3>
                <p>{invoice.description}</p>
              </div>
            </div>
          )}
        </div>

        <div className="line-items-section">
          <div className="piv-items-table-wrap">
          <table className="items-table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="col-center">Qty</th>
                <th className="col-right">Unit Price</th>
                <th className="col-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={index}>
                  <td>
                    <span className="piv-item-name">
                      <span className="piv-tile piv-tile--sm"><FileText size={15} /></span>
                      {item.description}
                    </span>
                  </td>
                  <td className="col-left" data-label="Qty">{item.quantity}</td>
                  {/* Stored as `unitPrice` by the create form and the API;
                      `price` is only here for anything older that used it.
                      Reading `price` alone showed every unit price as 0.00. */}
                  <td className="col-left" data-label="Unit Price">${formatUSD(item.unitPrice ?? item.price)}</td>
                  <td className="col-left" data-label="Amount">
                    ${formatUSD(item.amount || item.quantity * (item.unitPrice ?? item.price))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          <div className="totals-section">
            <div className="total-row">
              <span>Subtotal</span>
              <span>${formatUSD(invoice.metadata?.subtotal || invoice.items.reduce((sum, item) => sum + (item.amount || item.quantity * (item.unitPrice ?? item.price)), 0))}</span>
            </div>
            {console.log(invoice.metadata)}
            {invoice.metadata?.gstAmount > 0 && (
              <div className="total-row gst-row">
                <span>
                  GST/Tax {invoice.metadata?.gstType === 'percentage' ? `(${invoice.metadata?.gstValue}%)` : '(Fixed)'}
                </span>
                <span>${formatUSD(invoice.metadata.gstAmount)}</span>
              </div>
            )}
            <div className="total-row total-main">
              <span>Total</span>
              <span>${formatUSD(invoice.originalAmount)}</span>
            </div>
            {invoice.amount_paid > 0 && (
              <div className="total-row paid">
                <span>Amount Paid</span>
                <span className={invoice.amount_paid > invoice.originalAmount ? 'overpaid-text' : ''}>
                  ${formatUSD(invoice.amount_paid)}
                </span>
              </div>
            )}
            {invoice.status === 'overpaid' ? (
              <div className="total-row overpaid-row">
                <span>Overpaid By</span>
                <span>${formatUSD(invoice.amount_paid - invoice.originalAmount)}</span>
              </div>
            ) : (
              <div className="total-row remaining">
                <span className="total-row__label">
                  <span className="piv-tile piv-tile--sm"><Wallet size={15} /></span>
                  Amount Due
                </span>
                <span>${formatUSD(invoice.amount)}</span>
              </div>
            )}
          </div>
        </div>

        {transactions.length > 0 && (
          <div className="transactions-section">
            <h3>Payment History</h3>
            <div className="piv-tx-table-wrap">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Transaction</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, index) => (
                  <tr key={index}>
                    <td>{formatDate(tx.createdAt)}</td>
                    <td data-label="Amount">
                      {formatCrypto(tx.amount)} {tx.currency}
                      {tx.amountUsd > 0 && <small> (${formatUSD(tx.amountUsd)})</small>}
                    </td>
                    <td data-label="Status">
                      <span className={`tx-status ${tx.status?.toLowerCase()}`}>
                        {tx.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td data-label="Transaction">
                      {tx.txHash ? (
                        <a
                          href={`${tx.networkUrl}/tx/${tx.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tx-link"
                        >
                          {tx.txHash.slice(0, 10)}...
                          <ExternalLink size={12} />
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        <div className="action-section">
          {canPay && (
     <>
            <button className="btn-pay" onClick={handlePayNow}>
              <Wallet size={19} />
              Pay Now - ${formatUSD(invoice.amount)}
              <ArrowRight size={18} className="btn-pay__arrow" />
            </button>
            
     </> 
          )}
        </div>
        <div class="payment-footer"><p class="pc-powered"><span>Powered by</span><img src="/static/media/maxelpay-logo-dark.2181f7ed24c1199d42543c448b653011.svg" alt="MaxelPay"/></p></div>

        

        {(invoice.metadata?.clientNote || invoice.metadata?.termsConditions) && (
          <div className="invoice-notes">
            {invoice.metadata?.clientNote && (
              <div className="note-block">
                <h4>Note</h4>
                <p>{invoice.metadata.clientNote}</p>
              </div>
            )}
            {invoice.metadata?.termsConditions && (
              <div className="note-block">
                <h4>Terms &amp; Conditions</h4>
                <p>{invoice.metadata.termsConditions}</p>
              </div>
            )}
          </div>
        )}

        <div className="invoice-footer">
          <div className="piv-trust">
            <span className="piv-trust__item">
              <span className="piv-tile"><ShieldCheck size={18} /></span>
              <span>
                <strong>Powered by MaxelPay</strong>
                <em>Secure crypto payments</em>
              </span>
            </span>
            <span className="piv-trust__item">
              <span className="piv-tile"><Zap size={18} /></span>
              <span>
                <strong>No sign-up needed</strong>
                <em>Pay straight from your wallet</em>
              </span>
            </span>
            <span className="piv-trust__item">
              <span className="piv-tile"><Globe size={18} /></span>
              <span>
                <strong>Multiple blockchains</strong>
                <em>Global access</em>
              </span>
            </span>
          </div>
          <p className="liability-note">
            Invoice generated by MaxelPay. MaxelPay does not own, guarantee, or hold
            any liability for this payment.
          </p>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .public-invoice-page {
    min-height: 100vh;
    background-color: #ffffff; 
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    font-family: 'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  }

  /* Crisp tabular figures for all financial numbers */
  .public-invoice-page .num,
  .invoice-info-bar .info-item span,
  .items-table td.col-right,
  .items-table td.col-center,
  .total-row span:last-child {
    font-family: 'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace;
    font-variant-numeric: tabular-nums;
  }

  .invoice-container {
    background: white;
    border-radius: 24px;
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
    max-width: 640px;
    width: 100%;
    overflow: hidden;
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
  }

  .loading-state,
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    color: #6b7280;
    gap: 1rem;
    text-align: center;
    background: white;
    border-radius: 24px;
    padding: 4rem 3rem;
    max-width: 500px;
    width: 100%;
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
  }

  .error-state svg {
    color: #6b7280;
    margin-bottom: 0.5rem;
  }

  .error-state h2 {
    margin: 0;
    color: #1f2937;
    font-size: 1.75rem;
  }

  .error-state p {
    margin: 0;
    color: #6b7280;
    font-size: 1.1rem;
  }

  .spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #e5e7eb;
    border-top-color: #7c3aed;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .loading-state span {
    font-size: 1.1rem;
    color: #6b7280;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Header */
  .invoice-header {
    background: linear-gradient(135deg, #5847d6 0%,  #8270ff 100%);
    padding: 1.75rem 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .brand h1 {
    margin: 0;
    color: white;
    font-family: 'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
 
  .brand{
    width:100%;
  }

  .brand img{
    max-width: 170px;
    max-height: 56px;
    object-fit: contain;
    padding: 8px 0px;
    border-radius: 10px;
    vertical-align: center;
  }

  .brand-name{
    color: #fff;
    font-family: 'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  .invoice-footer .liability-note{
    max-width: 520px;
    margin: 0 auto;
    line-height: 1.5;
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 50px;
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: capitalize;
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }

  .status-badge.paid {
    background: rgba(16, 185, 129, 0.3);
    color: #a7f3d0;
  }

  .status-badge.pending,
  .status-badge.open {
    background: rgba(245, 158, 11, 0.3);
    color: #fef3c7;
  }

  .status-badge.partially_paid {
    background: rgba(59, 130, 246, 0.3);
    color: #bfdbfe;
  }

  .status-badge.overdue {
    background: rgba(239, 68, 68, 0.3);
    color: #fecaca;
  }

  .status-badge.overpaid {
    background: rgba(37, 99, 235, 0.3);
    color: #bfdbfe;
  }

  .status-icon {
    width: 16px;
    height: 16px;
  }

  .status-icon.success { color: #a7f3d0; }
  .status-icon.warning { color: #fef3c7; }
  .status-icon.danger { color: #fecaca; }
  .status-icon.overpaid { color: #bfdbfe; }

  /* Info Bar */
  .invoice-info-bar {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    padding: 1.5rem 2rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .info-item label {
    font-size: 0.7rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }

  .info-item span {
    font-size: 14px;
    color: #1f2937;
    font-weight: 500;
  }

  .info-item span.overdue {
    color: #ef4444;
  }

  .info-item.amount-due .amount {
    font-size: 14px;
    font-weight: 700;
    color: #5847d6;
  }

  .info-item.amount-due .amount.overpaid {
    color: #2563eb;
  }

  .info-item.amount-due .amount small {
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7280;
  }

  /* Bill To */
  .bill-to-section {
    padding: 10px 10px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .bill-to h3,
  .invoice-description h3 {
    margin: 0 0 0.5rem 0;
    font-size: 0.7rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }

  .customer-name {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #1f2937;
  }

  .customer-email {
    margin: 0.25rem 0 0 0;
    font-size: 0.9rem;
    color: #6b7280;
  }

  .invoice-description p {
    margin: 0;
    font-size: 0.95rem;
    color: #374151;
    line-height: 1.5;
  }

  /* Line Items */
  .line-items-section {
    padding: 1.5rem 2rem;
    overflow-y: scroll;
  }

  .items-table {
    width: 100%;
    border-collapse: collapse;
  }

  .items-table th {
    text-align: left;
    padding: 0.75rem 0.5rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    border-bottom: 2px solid #e5e7eb;
  }

  .items-table td {
    padding: 1rem 0.5rem;
    font-size: 0.95rem;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
  }

  .col-center { text-align: center; }
  .col-right { text-align: right; }

  /* Totals */
  .totals-section {
    margin-top: 10px;
    padding-top: 0;
    border-top: 2px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  .total-row {
    display: flex;
    justify-content: space-between;
    width: 250px;
    padding: 0.5rem 0;
    font-size: 0.95rem;
    color: #6b7280;
  }

  .total-row.total-main {
    font-weight: 600;
    color: #1f2937;
    font-size: 1.05rem;
    border-top: 1px solid #e5e7eb;
    margin-top: 0.5rem;
    padding-top: 0.75rem;
  }

  .total-row.paid span:last-child {
    color: #10b981;
  }

  .total-row.gst-row {
    color: #6b7280;
    font-size: 0.9rem;
  }

  .total-row.gst-row span:first-child {
    color: #4b5563;
  }

  .total-row.remaining {
    font-weight: 700;
    color: #5847d6;
    font-size: 1.15rem;
    background: linear-gradient(135deg, rgba(130, 112, 255, 0.12) 0%, rgba(88, 71, 214, 0.12) 100%);
    padding: 0.875rem 1.25rem;
    border-radius: 12px;
    margin-top: 0;
    width: 280px;
  }

  .total-row.overpaid-row {
    font-weight: 700;
    color: #2563eb;
    font-size: 1.15rem;
    background: rgba(37, 99, 235, 0.1);
    padding: 0.875rem 1.25rem;
    border-radius: 12px;
    margin-top: 0.75rem;
    width: 280px;
  }

  .overpaid-text {
    color: #2563eb !important;
    font-weight: 600;
  }

  /* Transactions */
  .transactions-section {
    padding: 1.5rem 2rem;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
  }

  .transactions-section h3 {
    margin: 0 0 1rem 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #1f2937;
  }

  .transactions-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border-radius: 12px;
    overflow: hidden;
  }

  .transactions-table th {
    text-align: left;
    padding: 0.75rem;
    font-size: 0.7rem;
    color: #6b7280;
    border-bottom: 1px solid #e5e7eb;
    text-transform: uppercase;
    font-weight: 600;
  }

  .transactions-table td {
    padding: 0.875rem 0.75rem;
    font-size: 0.85rem;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
  }

  .transactions-table tr:last-child td {
    border-bottom: none;
  }

  .transactions-table td small {
    color: #6b7280;
  }

  .tx-status {
    display: inline-flex;
    padding: 0.25rem 0.625rem;
    border-radius: 6px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
  }

  .tx-status.succeeded {
    background: #d1fae5;
    color: #059669;
  }

  .tx-status.pending {
    background: #fef3c7;
    color: #d97706;
  }

  .tx-status.failed,
  .tx-status.not_supported_token {
    background: #fee2e2;
    color: #dc2626;
  }

  .tx-link {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    color: #7c3aed;
    text-decoration: none;
    font-family: monospace;
    font-size: 0.8rem;
  }

  .tx-link:hover {
    text-decoration: underline;
  }

  /* Action Section */
  .action-section {
    padding: 1.75rem 2rem;
    display: flex;
    gap: 1rem;
    justify-content: center;
    /* border-top: 1px solid #e5e7eb; */
    background: white;
  }

  .btn-pay {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1.125rem 2.5rem;
    background: linear-gradient(135deg, #8270ff 0%, #5847d6 100%);
    color: white;
    border: none;
    border-radius: 14px;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 8px 25px rgba(88, 71, 214, 0.38);
  }

  .btn-pay:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(88, 71, 214, 0.48);
  }

  .btn-download {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1.125rem 1.75rem;
    background: white;
    color: #374151;
    border: 2px solid #e5e7eb;
    border-radius: 14px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-download:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    transform: translateY(-1px);
  }

  /* Footer */
  .invoice-notes {
    padding: 1.5rem 2rem 2rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .invoice-notes .note-block h4 {
    margin: 0 0 0.35rem;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #6b7280;
  }

  .invoice-notes .note-block p {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.55;
    color: #374151;
    white-space: pre-wrap;
  }

  .invoice-footer {
    padding: 1.25rem 2rem;
    text-align: center;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
    display:none;
  }

  .invoice-footer p {
    margin: 0;
    font-size: 0.85rem;
    color: #9ca3af;
  }

  .invoice-footer strong {
    color: #7c3aed;
  }

  /* Responsive */
  @media (max-width: 680px) {
    .public-invoice-page {
      padding: 0.75rem;
      align-items: flex-start;
    }

    .invoice-container {
      border-radius: 20px;
      max-height: none;
    }

    .invoice-header {
      padding: 1.25rem 1.5rem;
    }

    .invoice-info-bar {
      grid-template-columns: repeat(2, 1fr);
      padding: 1.25rem 1.5rem;
    }

    .bill-to-section {
      grid-template-columns: 1fr;
      padding: 1.25rem 1.5rem;
      gap: 1.25rem;
      overflow-y: scroll;
    }

    .line-items-section {
      padding: 1.25rem 1.5rem;
    }

    .transactions-section {
      padding: 1.25rem 1.5rem;
    }

    .action-section {
      flex-direction: column;
      padding: 1.5rem;
    }

    .btn-pay,
    .btn-download {
      width: 100%;
      justify-content: center;
    }

    .totals-section {
      align-items: stretch;
    }

    .total-row,
    .total-row.remaining {
      width: 100%;
    }

    .items-table th,
    .items-table td {
      padding: 0.625rem 0.375rem;
      font-size: 0.85rem;
    }

    .info-item.amount-due .amount {
      font-size: 1.25rem;
    }

    .loading-state,
    .error-state {
      padding: 3rem 2rem;
      border-radius: 20px;
    }
  }

  /* ══════════════════════════════════════════════════════════════════════
     Header, cards and footer -- MaxelPay violet, Poppins throughout.
     ══════════════════════════════════════════════════════════════════════ */

  .invoice-header {
    position: relative;
    overflow: hidden;
    /* MaxelPay violet. Kept as a two-stop gradient so the decorative layers
       below read against it at both ends. */
    background: linear-gradient(135deg, #5b3fd6 0%, #7c5cff 52%, #4f46e5 100%);
    padding: 15px 20px;
    display: block;
  }

  /* Decorative sweep. Pure CSS, so it costs no request and scales with the
     header; z-index 0 with the content at 1 keeps it strictly behind. */
  .piv-wave {
    position: absolute;
    top: -55%;
    right: -22%;
    width: 78%;
    height: 210%;
    z-index: 0;
    border-radius: 46% 54% 42% 58% / 52% 44% 56% 48%;
    background: linear-gradient(
      140deg,
      rgba(255, 255, 255, 0.16) 0%,
      rgba(255, 255, 255, 0.05) 48%,
      transparent 78%
    );
    animation: piv-wave-drift 14s ease-in-out infinite alternate;
  }

  @keyframes piv-wave-drift {
    from { transform: translate3d(0, 0, 0) rotate(0deg); }
    to   { transform: translate3d(-4%, 3%, 0) rotate(7deg); }
  }

  .piv-orb {
    position: absolute;
    z-index: 0;
    border-radius: 50%;
  }

  .piv-orb--a {
    top: 34%;
    right: 30%;
    width: 34px;
    height: 34px;
    background: rgba(255, 255, 255, 0.18);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.28);
  }

  .piv-orb--b {
    top: 22%;
    right: 6%;
    width: 18px;
    height: 18px;
    background: rgba(255, 255, 255, 0.14);
  }

  .piv-head-top,
  .piv-head-id,
  .piv-head-from,
  .piv-head-tags {
    position: relative;
    z-index: 1;
  }

  .piv-head-from {
    margin-top: 0;
    color: rgba(255, 255, 255, 0.82);
    font-size: 0.85rem;
  }

  .piv-head-from strong {
    color: #fff;
    font-weight: 600;
  }

  .piv-head-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 10px;
  }

  .brand {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
    min-width: 0;
  }

  /* Height-constrained with width: auto so the 604.5 x 125 lockup keeps its
     aspect ratio. */
  .brand-logo {
    // height: 26px;
    width: auto;
    display: block;
  }

  .brand-tag {
    color: rgba(255, 255, 255, 0.76);
    font-size: 0.76rem;
    font-weight: 400;
    letter-spacing: 0.01em;
  }

  /* The white-on-transparent mark, so no tile is needed against the gradient. */




  .piv-head-id__label {
    display: block;
    color: rgba(255, 255, 255, 0.78);
    font-size: 0.82rem;
    margin-bottom: 0.15rem;
    display:none;
  }

  .piv-head-id__value {
    display: block;
    color: #fff;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.015em;
    font-variant-numeric: tabular-nums;
  }

  .piv-head-tags {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin-top: 0;
    color: rgba(255, 255, 255, 0.82);
    font-size: 0.82rem;
    display:none;
  }

  .piv-head-tags i {
    font-style: normal;
    color: rgba(255, 255, 255, 0.5);
  }

  /* ── Icon tiles, shared by every card ── */

  .piv-tile {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 12px;
    background: rgba(124, 92, 255, 0.11);
    color: #5b3fd6;
  }

  .piv-tile--sm {
    width: 28px;
    height: 28px;
    border-radius: 9px;
  }

  /* ── Info bar and Bill To become cards with dividers ── */

  .invoice-info-bar,
  .bill-to-section {
    margin: 1rem 1.25rem 0;
    padding: 1rem 1.15rem;
    background: #fbfaff;
    border: 1px solid #ece9fb;
    border-radius: 16px;
  }

  .invoice-info-bar {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0;
  }

  .info-item,
  .bill-to,
  .invoice-description {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    padding: 0 1rem;
    min-width: 0;
  }

  /* Hairline separators between columns rather than gaps, so the row reads as
     one continuous strip. */
  .invoice-info-bar .info-item + .info-item,
  .bill-to-section .invoice-description {
    border-left: 1px solid #ebe8f7;
  }

  .info-item:first-child,
  .bill-to {
    padding-left: 0;
  }

  .info-item__body,
  .bill-to__body {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
  }

  .bill-to-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    border-bottom: 1px solid #ece9fb;
  }

  /* ── Line items card ── */

  .line-items-section {
    margin: 1rem 1.25rem 0;
    padding: 0.25rem 1.15rem 1.15rem;
    background: #fff;
    border: 1px solid #ece9fb;
    border-radius: 16px;
  }

  .piv-item-name {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
  }

  .total-row__label {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    font-size:14px;
  }

  /* ── Pay button ── */

  .btn-pay {
    width: auto;
    border-radius: 999px;
    font-family: inherit;
    background: linear-gradient(135deg, #7c5cff 0%, #5b3fd6 100%);
  }

  .btn-pay__arrow {
    transition: transform 180ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  .btn-pay:hover .btn-pay__arrow {
    transform: translateX(3px);
  }

  /* ── Footer trust row ── */

  .piv-trust {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0;
    margin-bottom: 0.9rem;
    text-align: left;
  }

  .piv-trust__item {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0 0.9rem;
    min-width: 0;
  }

  .piv-trust__item + .piv-trust__item {
    border-left: 1px solid #e7e4f5;
  }

  .piv-trust__item strong {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: #312e5f;
    line-height: 1.25;
  }

  .piv-trust__item em {
    font-style: normal;
    display: block;
    font-size: 0.72rem;
    color: #8b88a8;
    line-height: 1.25;
  }

  @media (max-width: 640px) {
    .invoice-info-bar,
    .bill-to-section,
    .piv-trust {
      grid-template-columns: 1fr;
    }

    /* Column separators become row separators once stacked; a left border on a
       full-width row reads as a stray vertical rule. */
    .invoice-info-bar .info-item + .info-item,
    .bill-to-section .invoice-description,
    .piv-trust__item + .piv-trust__item {
      border-left: none;
      border-top: 1px solid #ebe8f7;
      padding-top: 0.8rem;
      margin-top: 0.8rem;
    }

    .info-item,
    .bill-to,
    .invoice-description,
    .piv-trust__item {
      padding-left: 0;
      padding-right: 0;
    }

    .piv-head-id__value { font-size: 1.4rem; }
    .invoice-header { padding: 1.15rem 1.15rem 1.4rem; }
  }
    @media (max-width: 560px){
      .tx-status.succeeded{width:85px;}
    }

  @media (prefers-reduced-motion: reduce) {
    .piv-wave { animation: none; }
    .btn-pay:hover .btn-pay__arrow { transform: none; }
  }

`;

export default PublicInvoiceView;
